// screens/SuccessScreen.js
// BuzzInApp — Confirmación de canje + rating del comercio
//
// Mejoras v2:
//   • Card con animación de entrada (scale + fade)
//   • Fondo con halo verde radial
//   • Prize box más dramático (gradiente + borde acento)
//   • Divisor entre premio y rating
//   • Stars con hover effect en desktop
//   • Checkmark con drop-shadow verde

import React, { useState } from "react";
import { updateDocument, serverTimestamp, COLLECTIONS } from "../config/firebase";
import { applyTrustDelta } from "../security/fraudDetector";
import { TRUST_SCORE } from "../config/security";
import useTranslation from "../i18n/useTranslation";
import "./SuccessScreen.css";

const SuccessScreen = ({ redemption, business, authHook, onHome, onDispute }) => {
  const { t } = useTranslation();
  const { user } = authHook;

  const [rating, setRating]                   = useState(0);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingLoading, setRatingLoading]     = useState(false);

  const now  = new Date();
  const date = now.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = now.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });

  const handleRating = async (stars) => {
    if (ratingSubmitted || !redemption?.redemptionId) return;
    setRating(stars);
    setRatingLoading(true);
    try {
      await updateDocument(COLLECTIONS.REDEMPTIONS, redemption.redemptionId, {
        userRating: stars,
        ratedAt:    serverTimestamp(),
      });
      await applyTrustDelta(user.uid, TRUST_SCORE.DELTA.RATING_GIVEN, "rating_given");
      setRatingSubmitted(true);
    } catch (err) {
      console.error("[SuccessScreen] rating error:", err);
    } finally {
      setRatingLoading(false);
    }
  };

  return (
    <div className="success-screen">
      <div className="success-screen__card">

        {/* Checkmark con glow */}
        <div className="success-screen__check" aria-hidden="true">
          <svg viewBox="0 0 52 52" className="success-screen__check-svg">
            <circle className="success-screen__check-circle" cx="26" cy="26" r="25" fill="none"/>
            <path className="success-screen__check-mark" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
          </svg>
        </div>

        <h1 className="success-screen__title">{t("success.title")}</h1>
        <p className="success-screen__subtitle">{t("success.subtitle")}</p>

        {/* Premio */}
        <div className="success-screen__prize">
          <span className="success-screen__prize-text">
            {redemption?.offer?.prize_user ?? business?.prize_user}
          </span>
          <span className="success-screen__biz-name">
            {t("success.businessName", { business: business?.name })}
          </span>
        </div>

        {/* Timestamp */}
        <p className="success-screen__timestamp">{date} · {time}</p>

        <div className="success-screen__divider" aria-hidden="true" />

        {/* Rating */}
        {!ratingSubmitted ? (
          <div className="success-screen__rating">
            <p className="success-screen__rating-label">
              {t("success.ratingLabel", { business: business?.name })}
            </p>
            <div
              className="success-screen__stars"
              role="group"
              aria-label="Calificación del comercio"
            >
              {[1,2,3,4,5].map((star) => (
                <button
                  key={star}
                  className={`success-screen__star ${star <= rating ? "success-screen__star--active" : ""}`}
                  onClick={() => handleRating(star)}
                  disabled={ratingLoading}
                  aria-label={`${star} estrella${star > 1 ? "s" : ""}`}
                  aria-pressed={star <= rating}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p className="success-screen__rating-thanks">
            {t("success.ratingThanks")}
          </p>
        )}

      </div>

      {/* Acciones fuera de la card */}
      <div className="success-screen__actions">
        <button
          className="success-screen__btn success-screen__btn--primary"
          onClick={onHome}
        >
          {t("success.backHome")}
        </button>
        <button
          className="success-screen__btn success-screen__btn--link"
          onClick={onDispute}
        >
          {t("success.reportProblem")}
        </button>
      </div>
    </div>
  );
};

export default SuccessScreen;